import { Injectable } from '@angular/core';
import heic2any from 'heic2any';

@Injectable({
  providedIn: 'root'
})
export class HeicConverterService {

  constructor() { }

  /**
   * Converts a HEIC file to JPEG format
   * @param file The HEIC file to convert
   * @returns Promise<File> The converted JPEG file
   */
  async convertHeicToJpeg(file: File): Promise<File> {
    try {
      // Check if the file is a HEIC file
      if (!this.isHeicFile(file)) {
        return file; // Return original file if not HEIC
      }

      // Convert HEIC to JPEG using heic2any
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.8 // Good quality while maintaining reasonable file size
      }) as Blob;

      // Create a new file with JPEG extension
      const newFileName = this.replaceHeicExtension(file.name);
      const convertedFile = new File([convertedBlob], newFileName, {
        type: 'image/jpeg',
        lastModified: file.lastModified
      });

      return convertedFile;
    } catch (error) {
      console.error('Error converting HEIC file:', error);
      throw new Error('Failed to convert HEIC file to JPEG');
    }
  }

  /**
   * Converts multiple files, converting HEIC files to JPEG
   * @param files Array of files to process
   * @returns Promise<File[]> Array of processed files
   */
  async convertFiles(files: FileList): Promise<File[]> {
    const convertedFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const convertedFile = await this.convertHeicToJpeg(file);
        convertedFiles.push(convertedFile);
      } catch (error) {
        console.error(`Error converting file ${file.name}:`, error);
        // If conversion fails, keep the original file
        convertedFiles.push(file);
      }
    }

    return convertedFiles;
  }

  /**
   * Checks if a file is a HEIC file
   * @param file The file to check
   * @returns boolean True if the file is HEIC
   */
  private isHeicFile(file: File): boolean {
    const heicExtensions = ['.heic', '.heif', '.HEIC', '.HEIF'];
    const fileName = file.name.toLowerCase();
    
    return heicExtensions.some(ext => fileName.endsWith(ext.toLowerCase())) ||
           file.type === 'image/heic' ||
           file.type === 'image/heif';
  }

  /**
   * Replaces HEIC extension with JPEG extension
   * @param fileName The original filename
   * @returns string The filename with JPEG extension
   */
  private replaceHeicExtension(fileName: string): string {
    const heicExtensions = ['.heic', '.heif', '.HEIC', '.HEIF'];
    
    for (const ext of heicExtensions) {
      if (fileName.toLowerCase().endsWith(ext.toLowerCase())) {
        const baseName = fileName.substring(0, fileName.length - ext.length);
        return baseName + '.jpg';
      }
    }
    
    // If no HEIC extension found, append .jpg
    return fileName + '.jpg';
  }

  /**
   * Checks if the browser supports HEIC conversion
   * @returns boolean True if HEIC conversion is supported
   */
  isHeicConversionSupported(): boolean {
    return typeof heic2any !== 'undefined';
  }
} 